import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    RefreshCw, Search, Loader2, FileCheck, FileText, Send, Trash2, Mail, MessageSquare,
    ShieldCheck, Users, FolderOpen, X, ExternalLink, AlertTriangle, Eye, Download, CheckCircle2, XCircle, Eraser,
} from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../components/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';
import { OtpDossierReportModal, OtpDossierSummary, OtpSignerModal } from '../components/otp/OtpDossierReport';

/**
 * Firma Digitale v2 (/testotp, ADMIN/RESPONSABILE).
 *
 * Alternativa all'iframe di /firma-digitale: app.otpservice.io manda
 * X-Frame-Options: SAMEORIGIN e non è incorporabile. Qui tutto passa dalle API
 * OtpService tramite gli endpoint BE /api/otp/**.
 *
 * Il responsabile vede solo le pratiche dei mandati delle sue sedi; la rubrica
 * firmatari è unica per tutte le sedi e resta al titolare.
 *
 * Le risposte paginate hanno forma { page_count, total_count, dossiers|signers },
 * le altre sono array. Il rendering è difensivo: i campi mancanti diventano "—".
 */

type Tab = 'dossiers' | 'signers' | 'verify';

const STATE_LABEL: Record<string, { label: string; cls: string }> = {
    draft:                  { label: 'Bozza',             cls: 'bg-slate-100 text-slate-600' },
    just_created:           { label: 'Nuovo',             cls: 'bg-slate-100 text-slate-600' },
    notified:               { label: 'Notificata',        cls: 'bg-blue-50 text-blue-700' },
    viewed:                 { label: 'Visualizzata',      cls: 'bg-blue-50 text-blue-700' },
    signing:                { label: 'In firma',          cls: 'bg-amber-50 text-amber-700' },
    signed:                 { label: 'Firmata',           cls: 'bg-emerald-50 text-emerald-700' },
    signed_document_loaded: { label: 'PDF firmato pronto', cls: 'bg-emerald-50 text-emerald-700' },
    completed:              { label: 'Completata',        cls: 'bg-emerald-100 text-emerald-800' },
    rejected:               { label: 'Rifiutata',         cls: 'bg-rose-50 text-rose-700' },
    refused:                { label: 'Rifiutata',         cls: 'bg-rose-50 text-rose-700' },
    expired:                { label: 'Scaduta',           cls: 'bg-rose-50 text-rose-700' },
    canceled:               { label: 'Annullata',         cls: 'bg-slate-200 text-slate-600' },
    cancelled:              { label: 'Annullata',         cls: 'bg-slate-200 text-slate-600' },
};

// Stati del firmatario (recipient) visti nelle risposte reali.
Object.assign(STATE_LABEL, {
    accepted:              { label: 'Accettato',            cls: 'bg-blue-50 text-blue-700' },
    validating:            { label: 'In validazione',       cls: 'bg-amber-50 text-amber-700' },
    signed_document_sent:  { label: 'Firmato · copia inviata', cls: 'bg-emerald-50 text-emerald-700' },
});
const IDENT_LABEL: Record<string, string> = {
    recognized_de_visu: 'Riconosciuto di persona',
    recognized: 'Riconosciuto',
    recognized_remotely: 'Riconosciuto da remoto',
    to_be_validated: 'Da validare',
    waiting_validation: 'In attesa di validazione',
    rejected: 'Riconoscimento rifiutato',
};
// La validazione del riconoscimento si fa solo dall'interfaccia OtpService, non c'è API.
const needsValidation = (r: any) => /valid|pending|to_be|waiting/i.test(String(r?.identification_state || ''))
    && !/^recognized/i.test(String(r?.identification_state || ''));
const DOC_LABEL: Record<string, string> = { unsigned_document: 'Da firmare', signed_document: 'Firmato' };
const OTP_APP = 'https://app.otpservice.io';
const openOtpWindow = (path: string) => {
    // Apre una finestra a parte perché l'iframe è bloccato da X-Frame-Options.
    const w = Math.min(1100, Math.round(window.screen.availWidth * 0.55));
    window.open(OTP_APP + path, 'otpservice', `width=${w},height=${window.screen.availHeight},left=${window.screen.availWidth - w},top=0`);
};

const StateBadge: React.FC<{ state?: string }> = ({ state }) => {
    const s = String(state || '');
    const m = STATE_LABEL[s] || { label: s || '—', cls: 'bg-slate-100 text-slate-500' };
    return <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
};

const fmtDate = (v?: string) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const personName = (r: any) => [r?.first_name, r?.last_name ?? r?.['last name']].filter(Boolean).join(' ') || r?.email || '—';
const dossierState = (d: any) => d?.state ?? d?.current_state;
const dossierTitle = (d: any) =>
    d?.documents?.[0]?.name || d?.documents?.[0]?.filename || d?.title || d?.customer_contract_hash || `Dossier #${d?.id}`;
// Dossier creati dal gestionale: customer_contract_hash = "mandato_<id>".
const praticaIdFromHash = (d: any): string | null => {
    const m = /^mandato_(\d+)/i.exec(String(d?.customer_contract_hash || ''));
    return m ? m[1] : null;
};
const asList = (data: any, key: string): any[] =>
    Array.isArray(data) ? data : Array.isArray(data?.[key]) ? data[key] : [];

const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const TestOtpView: React.FC = () => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const isAdmin = user?.role === UserRole.ADMIN;
    const [tab, setTab] = useState<Tab>('dossiers');

    // ── Dossier ──────────────────────────────────────────────────────────────
    const [dossiers, setDossiers] = useState<any[]>([]);
    const [dPage, setDPage] = useState(1);
    const [dPageCount, setDPageCount] = useState(1);
    const [dTotal, setDTotal] = useState<number | null>(null);
    const [dSearch, setDSearch] = useState('');
    const [dLoading, setDLoading] = useState(false);
    const [dError, setDError] = useState<string | null>(null);

    const loadDossiers = useCallback(async (page = dPage, search = dSearch) => {
        setDLoading(true); setDError(null);
        try {
            const res = await api.get('/otp/dossiers', { params: { page, perPage: 25, search: search || undefined }, skipGlobalErrorToast: true });
            const data = res.data;
            setDossiers(asList(data, 'dossiers'));
            setDPageCount(Number(data?.page_count) || 1);
            setDTotal(data?.total_count != null ? Number(data.total_count) : null);
        } catch (e: any) {
            setDossiers([]);
            setDError(e?.response?.data?.message || e?.message || 'Errore caricamento pratiche OtpService');
        } finally { setDLoading(false); }
    }, [dPage, dSearch]);

    useEffect(() => { if (tab === 'dossiers') loadDossiers(dPage, dSearch); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, dPage]);

    // ── Dettaglio dossier ────────────────────────────────────────────────────
    const [detail, setDetail] = useState<any | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);
    const closePreview = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };

    const openDetail = async (id: number) => {
        setDetailLoading(true); setDetail({ id });
        try {
            const res = await api.get(`/otp/dossiers/${id}`, { skipGlobalErrorToast: true });
            setDetail(res.data || { id });
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Dettaglio non disponibile', 'error');
            setDetail(null);
        } finally { setDetailLoading(false); }
    };

    const run = async (key: string, fn: () => Promise<void>) => {
        if (busy) return;
        setBusy(key);
        try { await fn(); } catch (e: any) {
            // Con responseType 'blob' anche il JSON d'errore del BE arriva come Blob.
            let msg = e?.response?.data?.message;
            if (!msg && typeof Blob !== 'undefined' && e?.response?.data instanceof Blob) {
                try { msg = JSON.parse(await e.response.data.text())?.message; } catch { /* non JSON */ }
            }
            addToast(msg || e?.message || 'Operazione non riuscita', 'error');
        } finally { setBusy(null); }
    };

    const downloadSigned = (d: any) => run(`pdf-${d.id}`, async () => {
        const res = await api.get(`/otp/dossiers/${d.id}/signed-pdf`, { responseType: 'blob', skipGlobalErrorToast: true });
        downloadBlob(res.data, `Firmato_${praticaIdFromHash(d) ? `mandato_${praticaIdFromHash(d)}` : `dossier_${d.id}`}.pdf`);
    });
    const downloadAudit = (d: any) => run(`audit-${d.id}`, async () => {
        const res = await api.get(`/otp/dossiers/${d.id}/audit-trail`, { responseType: 'blob', skipGlobalErrorToast: true });
        const ct = String(res.headers?.['content-type'] || '');
        downloadBlob(res.data, `AuditTrail_dossier_${d.id}.${ct.includes('pdf') ? 'pdf' : ct.includes('zip') ? 'zip' : 'xml'}`);
    });
    const fetchDoc = async (d: any, doc: any): Promise<Blob> => {
        const res = await api.get(`/otp/dossiers/${d.id}/documents/${doc.id}`, { responseType: 'blob', skipGlobalErrorToast: true });
        return res.data;
    };
    const previewDoc = (d: any, doc: any) => run(`prev-${doc.id}`, async () => {
        const blob = await fetchDoc(d, doc);
        closePreview();
        setPreview({ url: URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })), name: doc.filename || `documento_${doc.id}.pdf` });
    });
    const downloadDoc = (d: any, doc: any) => run(`dl-${doc.id}`, async () => {
        downloadBlob(await fetchDoc(d, doc), doc.filename || `documento_${doc.id}.pdf`);
    });

    const notify = (d: any) => run(`notify-${d.id}`, async () => {
        await api.put(`/otp/dossiers/${d.id}/notify`, null, { skipGlobalErrorToast: true });
        addToast('Notifica reinviata ai firmatari', 'success');
        await openDetail(d.id);
    });
    const publish = (d: any) => run(`publish-${d.id}`, async () => {
        if (!window.confirm(`Inviare ai firmatari la pratica #${d.id} (ora in bozza)?`)) return;
        await api.put(`/otp/dossiers/${d.id}/publish`, null, { skipGlobalErrorToast: true });
        addToast('Pratica inviata ai firmatari', 'success');
        await openDetail(d.id); loadDossiers();
    });
    const remove = (d: any) => run(`del-${d.id}`, async () => {
        if (!window.confirm(`Eliminare definitivamente la pratica OtpService #${d.id}?\nPossibile solo se in bozza o "nuova".`)) return;
        await api.delete(`/otp/dossiers/${d.id}`, { skipGlobalErrorToast: true });
        addToast('Pratica eliminata', 'success');
        setDetail(null); loadDossiers();
    });
    const retry = (d: any, r: any, channel: 'email' | 'sms') => run(`${channel}-${r.id}`, async () => {
        await api.post(`/otp/dossiers/${d.id}/recipients/${r.id}/retry-${channel}`, null, { skipGlobalErrorToast: true });
        addToast(channel === 'email' ? 'Email reinviata' : 'SMS reinviato', 'success');
    });

    // ── Firmatari ────────────────────────────────────────────────────────────
    const [signers, setSigners] = useState<any[]>([]);
    const [sPage, setSPage] = useState(1);
    const [sPageCount, setSPageCount] = useState(1);
    const [sSearch, setSSearch] = useState('');
    const [sLoading, setSLoading] = useState(false);
    const loadSigners = useCallback(async (page = sPage, search = sSearch) => {
        setSLoading(true);
        try {
            const res = await api.get('/otp/signers', { params: { page, perPage: 25, search: search || undefined }, skipGlobalErrorToast: true });
            setSigners(asList(res.data, 'signers'));
            setSPageCount(Number(res.data?.page_count) || 1);
        } catch (e: any) {
            setSigners([]);
            addToast(e?.response?.data?.message || 'Errore caricamento firmatari', 'error');
        } finally { setSLoading(false); }
    }, [sPage, sSearch, addToast]);
    useEffect(() => { if (tab === 'signers') loadSigners(sPage, sSearch); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, sPage]);

    // Scheda firmatario (rubrica): vedi / svuota dati identificativi / elimina.
    const [signerDetail, setSignerDetail] = useState<any | null>(null);
    const [signerLoading, setSignerLoading] = useState(false);
    const openSigner = async (id: number) => {
        setSignerLoading(true); setSignerDetail({ id });
        try {
            const res = await api.get(`/otp/signers/${id}`, { skipGlobalErrorToast: true });
            setSignerDetail(res.data || { id });
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Firmatario non disponibile', 'error');
            setSignerDetail(null);
        } finally { setSignerLoading(false); }
    };
    const clearSigner = (s: any) => run(`clear-${s.id}`, async () => {
        if (!window.confirm(`Svuotare i dati identificativi di ${personName(s)}?\n\nVerranno cancellati documento d'identità, foto, codice fiscale, nascita e residenza. Nome, email e telefono restano. Le firme già fatte non cambiano.`)) return;
        const res = await api.post(`/otp/signers/${s.id}/clear-identity`, null, { skipGlobalErrorToast: true });
        if (res.data?.anagrafica_azzerata === false) {
            addToast(`Documento e foto cancellati. OtpService non ha permesso di cancellare l'anagrafica${res.data?.motivo ? `: ${res.data.motivo}` : ''}`, 'info');
        } else {
            addToast('Dati identificativi cancellati', 'success');
        }
        await openSigner(s.id);
    });
    const deleteSigner = (s: any) => run(`sdel-${s.id}`, async () => {
        if (!window.confirm(`Eliminare definitivamente ${personName(s)} dalla rubrica OtpService?\nPossibile solo se non è mai stato usato in una pratica.`)) return;
        await api.delete(`/otp/signers/${s.id}`, { skipGlobalErrorToast: true });
        addToast('Firmatario eliminato', 'success');
        setSignerDetail(null); loadSigners();
    });

    // ── Verifica firma ───────────────────────────────────────────────────────
    const [checksum, setChecksum] = useState('');
    const [verifyRes, setVerifyRes] = useState<any | null>(null);
    const [verifying, setVerifying] = useState(false);
    // Scheda completa "per il cliente" (verifica firma o dettaglio pratica).
    const [report, setReport] = useState<any | null>(null);
    const verify = async () => {
        const c = checksum.trim();
        if (!c || verifying) return;
        setVerifying(true); setVerifyRes(null);
        try {
            const res = await api.get(`/otp/signatures/verify/${encodeURIComponent(c)}`, { skipGlobalErrorToast: true });
            setVerifyRes(res.data ?? {});
        } catch (e: any) {
            setVerifyRes({ __error: e?.response?.data?.message || 'Codice non valido o firma non trovata' });
        } finally { setVerifying(false); }
    };

    const TabBtn: React.FC<{ id: Tab; icon: React.ReactNode; label: string }> = ({ id, icon, label }) => (
        <button onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === id ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
            {icon}{label}
        </button>
    );

    const Pager: React.FC<{ page: number; count: number; set: (p: number) => void }> = ({ page, count, set }) => (
        <div className="flex items-center justify-end gap-2 text-xs text-slate-500">
            <button disabled={page <= 1} onClick={() => set(page - 1)} className="px-2.5 py-1 rounded border border-slate-200 disabled:opacity-40">‹</button>
            <span>Pagina {page} di {Math.max(1, count)}</span>
            <button disabled={page >= count} onClick={() => set(page + 1)} className="px-2.5 py-1 rounded border border-slate-200 disabled:opacity-40">›</button>
        </div>
    );

    return (
        <div className="p-6 space-y-5">
            <div>
                <h1 className="text-2xl font-extrabold text-slate-900">Firma Digitale (v2)</h1>
                <p className="text-xs text-slate-500 mt-1">
                    Gestione delle firme OtpService tramite API: pratiche, firmatari e verifica firma, anche quando il pannello OtpService non si carica nel gestionale.
                </p>
            </div>

            <div className="flex flex-wrap gap-2">
                <TabBtn id="dossiers" icon={<FolderOpen size={15} />} label="Pratiche" />
                {isAdmin && <TabBtn id="signers" icon={<Users size={15} />} label="Firmatari" />}
                <TabBtn id="verify" icon={<ShieldCheck size={15} />} label="Verifica firma" />
            </div>

            {tab === 'dossiers' && (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
                    <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
                        <form className="flex items-center gap-2 flex-1 min-w-[220px]" onSubmit={e => { e.preventDefault(); setDPage(1); loadDossiers(1, dSearch); }}>
                            <div className="relative flex-1">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input value={dSearch} onChange={e => setDSearch(e.target.value)} placeholder="Cerca per firmatario o codice…"
                                    className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <button type="submit" className="px-3 py-2 rounded-lg bg-slate-800 text-white text-sm font-bold">Cerca</button>
                        </form>
                        <button onClick={() => loadDossiers()} disabled={dLoading} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                            {dLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aggiorna
                        </button>
                        {dTotal != null && <span className="text-xs text-slate-400">{dTotal} pratiche</span>}
                    </div>

                    {dError ? (
                        <div className="p-6 flex items-start gap-3 text-sm text-rose-700"><AlertTriangle size={18} className="shrink-0" /><span>{dError}</span></div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="p-3 text-left">#</th>
                                        <th className="p-3 text-left">Documento</th>
                                        <th className="p-3 text-left">Firmatari</th>
                                        <th className="p-3 text-left">Stato</th>
                                        <th className="p-3 text-left">Mandato</th>
                                        <th className="p-3 text-left">Creata</th>
                                        <th className="p-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {dLoading && dossiers.length === 0 && (
                                        <tr><td colSpan={7} className="p-6 text-center text-slate-400"><Loader2 size={18} className="animate-spin inline" /></td></tr>
                                    )}
                                    {!dLoading && dossiers.length === 0 && (
                                        <tr><td colSpan={7} className="p-6 text-center text-slate-400">Nessuna pratica</td></tr>
                                    )}
                                    {dossiers.map(d => {
                                        const pid = praticaIdFromHash(d);
                                        return (
                                            <tr key={d.id} className="hover:bg-slate-50">
                                                <td className="p-3 font-mono text-xs text-slate-400">{d.id}</td>
                                                <td className="p-3 font-semibold text-slate-800 max-w-[260px] truncate">{dossierTitle(d)}</td>
                                                <td className="p-3 text-slate-600">{(d.recipients || []).map(personName).join(', ') || '—'}</td>
                                                <td className="p-3"><StateBadge state={dossierState(d)} /></td>
                                                <td className="p-3">{pid ? <Link to={`/pratiche/${pid}`} className="text-indigo-600 font-semibold hover:underline">#{pid}</Link> : <span className="text-slate-300">—</span>}</td>
                                                <td className="p-3 text-xs text-slate-500">{fmtDate(d.created_at)}</td>
                                                <td className="p-3 text-right">
                                                    <button onClick={() => openDetail(d.id)} className="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-bold hover:bg-indigo-100">Dettaglio</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="p-3 border-t border-slate-100"><Pager page={dPage} count={dPageCount} set={setDPage} /></div>
                </div>
            )}

            {tab === 'signers' && (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
                    <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
                        <form className="flex items-center gap-2 flex-1 min-w-[220px]" onSubmit={e => { e.preventDefault(); setSPage(1); loadSigners(1, sSearch); }}>
                            <div className="relative flex-1">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input value={sSearch} onChange={e => setSSearch(e.target.value)} placeholder="Cerca firmatario…"
                                    className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <button type="submit" className="px-3 py-2 rounded-lg bg-slate-800 text-white text-sm font-bold">Cerca</button>
                        </form>
                        <button onClick={() => loadSigners()} disabled={sLoading} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                            {sLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aggiorna
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                                <tr><th className="p-3 text-left">#</th><th className="p-3 text-left">Nome</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Telefono</th><th className="p-3 text-right">Azioni</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {!sLoading && signers.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">Nessun firmatario</td></tr>}
                                {signers.map(s => (
                                    <tr key={s.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openSigner(s.id)}>
                                        <td className="p-3 font-mono text-xs text-slate-400">{s.id}</td>
                                        <td className="p-3 font-semibold text-slate-800">{personName(s)}</td>
                                        <td className="p-3 text-slate-600">{s.email || '—'}</td>
                                        <td className="p-3 text-slate-600">{s.phone_number ? `${s.phone_prefix ? '+' + s.phone_prefix + ' ' : ''}${s.phone_number}` : '—'}</td>
                                        <td className="p-3" onClick={e => e.stopPropagation()}>
                                            <div className="flex justify-end gap-1.5">
                                                <button title="Vedi dati" onClick={() => openSigner(s.id)} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"><Eye size={14} /></button>
                                                <button title="Svuota dati identificativi" onClick={() => clearSigner(s)} disabled={!!busy} className="p-2 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                                                    {busy === `clear-${s.id}` ? <Loader2 size={14} className="animate-spin" /> : <Eraser size={14} />}
                                                </button>
                                                <button title="Elimina firmatario" onClick={() => deleteSigner(s)} disabled={!!busy} className="p-2 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                                                    {busy === `sdel-${s.id}` ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-3 border-t border-slate-100"><Pager page={sPage} count={sPageCount} set={setSPage} /></div>
                </div>
            )}

            {tab === 'verify' && (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-4 max-w-2xl">
                    <p className="text-sm text-slate-600">Inserisci il <b>codice di verifica firma</b> riportato sul documento firmato.</p>
                    <form className="flex gap-2" onSubmit={e => { e.preventDefault(); verify(); }}>
                        <input value={checksum} onChange={e => setChecksum(e.target.value)} placeholder="Codice di verifica…"
                            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-500" />
                        <button type="submit" disabled={verifying || !checksum.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold disabled:opacity-50">
                            {verifying ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Verifica
                        </button>
                    </form>
                    {verifyRes && (verifyRes.__error ? (
                        <div className="text-sm text-rose-700 flex items-center gap-2"><AlertTriangle size={16} />{verifyRes.__error}</div>
                    ) : (
                        <div className="space-y-3">
                            {verifyRes.checksum_valid === true && <div className="flex items-center gap-2 text-emerald-700 font-bold"><CheckCircle2 size={18} /> Codice valido: il documento risulta firmato su OtpService</div>}
                            {verifyRes.checksum_valid === false && <div className="flex items-center gap-2 text-rose-700 font-bold"><XCircle size={18} /> Codice NON valido</div>}
                            {Array.isArray(verifyRes.errors) && verifyRes.errors.length > 0 && <p className="text-xs text-slate-500">Nota OtpService: {verifyRes.errors.join(' ')}</p>}
                            {/* La verifica restituisce la pratica firmata (dossier) → riepilogo + scheda completa. */}
                            {(verifyRes.dossier || (verifyRes.id && (verifyRes.recipients || verifyRes.documents))) && (
                                <OtpDossierSummary dossier={verifyRes.dossier || verifyRes} onOpen={() => setReport(verifyRes.dossier || verifyRes)} />
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Pannello dettaglio dossier */}
            {detail && (
                <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => !busy && setDetail(null)}>
                    <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Pratica OtpService #{detail.id}</p>
                                <h2 className="text-lg font-extrabold text-slate-900 truncate">{detailLoading ? 'Caricamento…' : dossierTitle(detail)}</h2>
                                {!detailLoading && <div className="mt-1 flex items-center gap-2"><StateBadge state={dossierState(detail)} />
                                    {praticaIdFromHash(detail) && <Link to={`/pratiche/${praticaIdFromHash(detail)}`} className="text-xs text-indigo-600 font-semibold inline-flex items-center gap-1 hover:underline">Mandato #{praticaIdFromHash(detail)} <ExternalLink size={11} /></Link>}
                                </div>}
                            </div>
                            <button onClick={() => !busy && setDetail(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={18} /></button>
                        </div>

                        {detailLoading ? (
                            <div className="p-10 text-center text-slate-400"><Loader2 size={22} className="animate-spin inline" /></div>
                        ) : (
                            <div className="p-5 space-y-5">
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    <div><p className="text-slate-400 uppercase font-bold tracking-wider">Tipo</p><p className="text-slate-700">{detail.type || '—'}</p></div>
                                    <div><p className="text-slate-400 uppercase font-bold tracking-wider">Creata</p><p className="text-slate-700">{fmtDate(detail.created_at)}</p></div>
                                    <div><p className="text-slate-400 uppercase font-bold tracking-wider">Firmata il</p><p className="text-slate-700">{fmtDate(detail.signed_at)}</p></div>
                                    <div><p className="text-slate-400 uppercase font-bold tracking-wider">Scadenza pratica</p><p className="text-slate-700">{fmtDate(detail.dossier_expiration_date)}</p></div>
                                    <div className="col-span-2"><p className="text-slate-400 uppercase font-bold tracking-wider">Codice di verifica firma</p><p className="text-slate-700 font-mono break-all">{detail.final_checksum || '—'}</p></div>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <button onClick={() => downloadSigned(detail)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold disabled:opacity-50">
                                        {busy === `pdf-${detail.id}` ? <Loader2 size={13} className="animate-spin" /> : <FileCheck size={13} />} PDF firmato
                                    </button>
                                    <button onClick={() => downloadAudit(detail)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-xs font-bold disabled:opacity-50">
                                        {busy === `audit-${detail.id}` ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} Audit trail
                                    </button>
                                    <button onClick={() => openOtpWindow(`/dossiers/${detail.id}`)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-200 text-indigo-700 text-xs font-bold hover:bg-indigo-50">
                                        <ExternalLink size={13} /> {(detail.recipients || []).some(needsValidation) ? 'Valida su OtpService' : 'Apri su OtpService'}
                                    </button>
                                    <button onClick={() => notify(detail)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-xs font-bold disabled:opacity-50">
                                        {busy === `notify-${detail.id}` ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Reinvia notifica
                                    </button>
                                    {['draft', 'just_created'].includes(String(dossierState(detail))) && (
                                        <>
                                            <button onClick={() => publish(detail)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white text-xs font-bold disabled:opacity-50">
                                                {busy === `publish-${detail.id}` ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Invia ai firmatari
                                            </button>
                                            <button onClick={() => remove(detail)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-rose-600 text-white text-xs font-bold disabled:opacity-50">
                                                {busy === `del-${detail.id}` ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Elimina
                                            </button>
                                        </>
                                    )}
                                </div>

                                <div>
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Documenti</p>
                                    <div className="space-y-2">
                                        {(detail.documents || []).length === 0 && <p className="text-sm text-slate-400">Nessun documento</p>}
                                        {(detail.documents || []).map((doc: any) => (
                                            <div key={doc.id} className="border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-slate-800 text-sm truncate">{doc.filename || `Documento #${doc.id}`}</p>
                                                    <p className="text-xs text-slate-500">
                                                        <span className={`font-bold ${doc.label === 'signed_document' ? 'text-emerald-700' : 'text-slate-600'}`}>{DOC_LABEL[doc.label] || doc.label || '—'}</span>
                                                        {doc.pages_count ? ` · ${doc.pages_count} pagine` : ''} · {fmtDate(doc.created_at)}
                                                    </p>
                                                </div>
                                                <div className="flex gap-1.5 shrink-0">
                                                    <button title="Anteprima" onClick={() => previewDoc(detail, doc)} disabled={!!busy} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                                        {busy === `prev-${doc.id}` ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                                                    </button>
                                                    <button title="Scarica" onClick={() => downloadDoc(detail, doc)} disabled={!!busy} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                                        {busy === `dl-${doc.id}` ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Firmatari</p>
                                    <div className="space-y-2">
                                        {(detail.recipients || []).length === 0 && <p className="text-sm text-slate-400">Nessun firmatario</p>}
                                        {(detail.recipients || []).map((r: any) => (
                                            <div key={r.id ?? r.uuid} className="border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-slate-800 text-sm truncate">{personName(r)}</p>
                                                    <p className="text-xs text-slate-500 truncate">{[r.email, r.phone_number].filter(Boolean).join(' · ') || '—'}</p>
                                                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                        <StateBadge state={r.state ?? r.current_state} />
                                                        {r.identification_state && (
                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${needsValidation(r) ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                                                                {IDENT_LABEL[r.identification_state] || r.identification_state}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {/* Cronologia del firmatario */}
                                                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
                                                        {[
                                                            ['Notificato', r.notified_at], ['Visualizzato', r.viewed_at], ['Accettato', r.accepted_at],
                                                            ['Validazione', r.validating_at], ['Firmato', r.signed_at], ['Copia firmata inviata', r.signed_document_sent_at],
                                                        ].filter(([, v]) => !!v).map(([k, v]) => (
                                                            <div key={k as string}><span className="text-slate-400">{k}:</span> {fmtDate(v as string)}</div>
                                                        ))}
                                                        {r.ip_signing && <div><span className="text-slate-400">IP firma:</span> {r.ip_signing}</div>}
                                                        {r.otp_channel && <div><span className="text-slate-400">OTP via:</span> {r.otp_channel}</div>}
                                                    </div>
                                                </div>
                                                {r.id != null && (
                                                    <div className="flex gap-1.5 shrink-0">
                                                        <button title="Reinvia email" onClick={() => retry(detail, r, 'email')} disabled={!!busy} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                                            {busy === `email-${r.id}` ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                                                        </button>
                                                        <button title="Reinvia SMS" onClick={() => retry(detail, r, 'sms')} disabled={!!busy} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                                            {busy === `sms-${r.id}` ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {((detail.email_communications || []).length > 0 || (detail.sms_communications || []).length > 0) && (
                                    <div>
                                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Comunicazioni</p>
                                        <div className="space-y-1 text-xs text-slate-600">
                                            {(detail.email_communications || []).map((c: any) => (
                                                <div key={`e-${c.id}`} className="flex items-center gap-2"><Mail size={12} className="text-slate-400" />
                                                    Email {c.label === 'signed_document' ? 'documento firmato' : c.label === 'unsigned_document' ? 'documento da firmare' : c.label} · {fmtDate(c.sent_at || c.created_at)}</div>
                                            ))}
                                            {(detail.sms_communications || []).map((c: any) => (
                                                <div key={`s-${c.id}`} className="flex items-center gap-2"><MessageSquare size={12} className="text-slate-400" />
                                                    SMS {c.label === 'otp' ? 'codice OTP' : c.label} · inviato {fmtDate(c.sent_at || c.created_at)}{c.delivered_at ? ` · consegnato ${fmtDate(c.delivered_at)}` : ''}</div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button onClick={() => setReport(detail)} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm font-bold hover:bg-indigo-100">
                                    <Eye size={15} /> Apri scheda completa della firma
                                </button>

                                <details className="text-xs">
                                    <summary className="cursor-pointer text-slate-400 font-semibold">Dati grezzi (debug)</summary>
                                    <pre className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-x-auto max-h-96">{JSON.stringify(detail, (k, v) => (k === 'otp' ? undefined : v), 2)}</pre>
                                </details>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {report && <OtpDossierReportModal dossier={report} onClose={() => setReport(null)} />}
            {signerDetail && (
                <OtpSignerModal signer={signerDetail} loading={signerLoading} busy={busy}
                    onClose={() => !busy && setSignerDetail(null)}
                    onClear={() => clearSigner(signerDetail)} onDelete={() => deleteSigner(signerDetail)} />
            )}
            {preview && (
                <div className="fixed inset-0 z-[60] bg-black/70 flex flex-col p-4" onClick={closePreview}>
                    <div className="flex items-center justify-between text-white mb-2" onClick={e => e.stopPropagation()}>
                        <span className="font-semibold truncate">{preview.name}</span>
                        <div className="flex gap-2">
                            <a href={preview.url} download={preview.name} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold inline-flex items-center gap-1"><Download size={13} /> Scarica</a>
                            <button onClick={closePreview} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20"><X size={16} /></button>
                        </div>
                    </div>
                    <iframe src={preview.url} title={preview.name} className="flex-1 w-full rounded-lg bg-white" onClick={e => e.stopPropagation()} />
                </div>
            )}
        </div>
    );
};

export default TestOtpView;
